import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Load user from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('goldmart_token');
      const storedUser = localStorage.getItem('goldmart_user');

      if (token && storedUser) {
        try {
          // Verify token with backend
          const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
          } else {
            // Token is invalid, clear storage
            localStorage.removeItem('goldmart_token');
            localStorage.removeItem('goldmart_user');
            localStorage.removeItem('goldmart_admin'); // Legacy key
          }
        } catch (error) {
          console.error('Error verifying token:', error);
          localStorage.removeItem('goldmart_token');
          localStorage.removeItem('goldmart_user');
          localStorage.removeItem('goldmart_admin');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token and user data
      localStorage.setItem('goldmart_token', data.token);
      localStorage.setItem('goldmart_user', JSON.stringify(data.user));
      localStorage.setItem('goldmart_admin', 'true'); // For backward compatibility
      
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('goldmart_token');
    localStorage.removeItem('goldmart_user');
    localStorage.removeItem('goldmart_admin');
    setUser(null);
    navigate('/login');
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isBranchManager = () => {
    return user?.role === 'branch_manager';
  };

  const getUserBranchId = () => {
    return user?.branchId || null;
  };

  const hasAccessToBranch = (branchId) => {
    if (isAdmin()) return true; // Admin has access to all branches
    return user?.branchId === branchId;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin,
    isBranchManager,
    getUserBranchId,
    hasAccessToBranch,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

